import { Injectable } from '@angular/core';
import { PartialIDynamicItem } from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { selectConfigurationsState } from '@epicuro-next/platform/configuration';
import { getValue } from '@epicuro-next/utilities';
import { Store } from '@ngrx/store';
import { map, Observable, take } from 'rxjs';
import { IFetchConfigService } from '../fetch-config.service';

type UnpackedKeyResult = {
  namespace: string;
  ruleName: string;
  objectPath?: string;
};

@Injectable({
  providedIn: 'root',
})
export class FetchConfigFromRuleEngineService implements IFetchConfigService {
  private readonly selectConfigurationsState$ = this.store.select(
    selectConfigurationsState,
  );

  constructor(private readonly store: Store) {}
  public canFetchFrom(source: string): boolean {
    source = source.toLowerCase().trim();
    return ['rule-engine', 'ruleengine'].includes(source);
  }

  public fetch(key: string): Observable<PartialIDynamicItem[]> {
    return this.selectConfigurationsState$.pipe(
      take(1),
      map((configuration) => {
        const unpackedKey = this.unpackKey(key);
        const namespace = configuration[unpackedKey.namespace];
        if (namespace === undefined) {
          return undefined;
        }

        const ruleValue = namespace[unpackedKey.ruleName];
        if (ruleValue === undefined) {
          return undefined;
        }

        if (typeof ruleValue !== 'string') {
          throw new Error(
            `Expected a string for namespace '${
              unpackedKey.namespace
            }' and rule name '${
              unpackedKey.ruleName
            }', but got '${typeof ruleValue}'.`,
          );
        }

        const configurationValueString = ruleValue;
        let configurationValue = JSON.parse(configurationValueString);

        if (unpackedKey.objectPath) {
          configurationValue = getValue(
            configurationValue,
            unpackedKey.objectPath,
          );
        }

        return configurationValue;
      }),
    );
  }

  private unpackKey(key: string): UnpackedKeyResult {
    const splitResult = key.split('.');
    if (splitResult.length < 2) {
      throw new Error(
        `Expected key '${key}' to have at least 2 entires like 'namespace.ruleName', e.g. 'pulse.recommendations-panels'.`,
      );
    }

    const namespace = splitResult[0];
    const ruleName = splitResult[1];
    const result: UnpackedKeyResult = {
      namespace,
      ruleName,
    };

    if (splitResult.length > 2) {
      const objectPath = splitResult.slice(2).join('.');
      result.objectPath = objectPath;
    }

    return result;
  }
}
